/*
 * 작성자 : 김광민
 * 날짜 : 2024.07.04
 * 기능 : 상황판에서 가로 사이즈 5 기상종합 컴포넌트 중 시간별날씨
 */

import styled from 'styled-components';
import { getWeatherIcon as getWeatherIconUtil } from '../../utils/weatherUtils';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start !important;
  align-items: flex-start !important;
  padding: 1.5rem 0;
  .title {
    height: 4rem;
    width: 100%;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--filled-blue-deep);
  }
  .weatherTime {
    width: 100%;
    flex-grow: 1;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 0.5rem;
    .weatherData {
      display: flex;
      flex-direction: column;
      align-items: center;
      background: var(--widget-weather-item-bg);
      // background-color: var(--alpha-25);
      border-radius: 0.5rem;
      padding-top: 1rem;
      .data {
        display: flex;
        align-items: center;
        font-size: 1.125rem;
        font-weight: 700;
        color: var(--filled-blue-deep);
        color: var(--inverse-alpha-50);
        &.time {
          font-weight: 600;
          color: var(--text-secondary);
          color: var(--text-primary);
        }
      }
      .info {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }
      .wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        position: relative;
        overflow: visible;
        &:nth-child(4) {
          margin-top: 1rem;
        }
        &:nth-child(5) {
          margin-top: 1.5rem;
        }
        .icon-wrapper {
          flex-grow: 1;
          position: absolute;
          height: 1.5rem;
          width: 1.5rem;
          top: 0;
          top: -0.75rem;
          left: 50%;
          transform: translate(-50%);
          background-color: var(--text-reverse);
          // background-color: var(--board);
          border-radius: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0;
          &.rain {
            font-size: 1rem;
            color: var(--widget-status-blue);
          }
          &.temp {
            color: var(--widget-status-red);
            font-size: 1.25rem;
          }
        }
        .wrapper-data {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--widget-weather-item-sub-bg);
          // background-color: var(--board);
          padding: 1rem 0.5rem;
          padding-top: 1.25rem;
          border-radius: 0.5rem;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
          .unit {
            font-size: 0.875rem;
            margin-left: 1px;
            color: var(--text-tertiary);
            font-weight: 500;
          }
        }
      }
      .icon {
        flex-grow: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem 0.5rem;

        .no-weather-icon {
          color: var(--widget-text-primary);
        }
      }
    }
  }

  &.medium {
    .weatherTime {
      margin: 1rem 0;
      width: 100%;
      flex-grow: 1;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      padding-left: 0;
      .ampm {
        display: none;
      }
    }
    .weatherData {
      padding-top: 0.5rem !important;
      padding-bottom: 0 !important;
      .icon {
        padding: 0 0.75rem;
        margin-bottom: 1rem;
      }
      .info {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        background-color: var(--widget-secondary-transparent);
        border-radius: 0.5rem;
        width: 100%;
        gap: 0;
        .wrapper-data {
          background-color: transparent;
          padding: 0.75rem 0;
          padding-top: 1rem;
          font-size: 1.125rem; // 18px
          font-size: 1.0625rem; // 17px
        }
      }
    }
  }
`;

const getWeatherIconComponent = (weather: string) => {
  return getWeatherIconUtil(weather, 48);
};

interface TimeInfo {
  ampm: '오전' | '오후';
  hour: number;
  minute: string;
}

function getTime(milliseconds: string): TimeInfo {
  const hour = Number(milliseconds.slice(0, 2));
  const minute = milliseconds.slice(2, 4);
  return {
    ampm: hour >= 12 ? '오후' : '오전',
    hour: hour >= 12 ? hour - 12 : hour,
    minute,
  };
}

type Props = {
  timeWeather: TimeWeatherProps[];
  width: 'large' | 'medium';
};

type TimeWeatherProps = {
  date: string;
  pcp: string;
  pop: string;
  sky: string;
  sno: string;
  time: string;
  wsd: string;
  tmp: string;
};

const WeatherWideTime = ({ timeWeather, width }: Props) => {
  return (
    <Root className={width}>
      {/* <div className='title'>{t('시간대별 날씨')}</div> */}
      <div className='weatherTime'>
        {timeWeather.map((weather: TimeWeatherProps) => {
          const { pop, sky, time, tmp } = weather;
          const { ampm, hour, minute } = getTime(time);
          return (
            <div key={time} className='weatherData'>
              <div className='data ampm'>{ampm}</div>
              <div className='data time'>{`${hour}:${minute}`}</div>
              <div className='icon'>{getWeatherIconComponent(sky)}</div>
              <div className='info'>
                <div className='wrapper'>
                  <div className='icon-wrapper'>
                    <span className='material-symbols-rounded temp'>thermometer</span>
                  </div>
                  <div className='wrapper-data'>
                    {tmp} <span className='unit'>°</span>
                  </div>
                </div>
                <div className='wrapper '>
                  <div className='icon-wrapper'>
                    <span className='material-symbols-rounded rain'>water_drop</span>
                  </div>
                  <div className='wrapper-data'>
                    {pop}
                    <span className='unit'>%</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Root>
  );
};

export default WeatherWideTime;
